import { useEffect, useState } from 'react';
import { HardDrive, AlertTriangle, RefreshCw, Activity } from 'lucide-react';

interface Drive {
  id: string;
  serial: string;
  model: string;
  vendor: string;
  type: string;
  interface: string;
  capacityBytes: number;
  health: 'healthy' | 'warning' | 'failed' | 'missing' | 'rebuilding' | 'spare' | 'offline';
  smartStatus: string;
  temperatureC: number;
  powerOnHours: number;
  pool: string | null;
  role: string;
  slot: number | null;
}

const formatBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(2)} ${units[i]}`;
};

const healthBadge = (health: string) => {
  const map: Record<string, string> = {
    healthy: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
    warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    failed: 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
    rebuilding: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    spare: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    offline: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    missing: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  };
  return map[health] || map.healthy;
};

export default function Drives() {
  const [drives, setDrives] = useState<Drive[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/v1/drives', { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => {
        setDrives(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const summary = {
    total: drives.length,
    healthy: drives.filter((d) => d.health === 'healthy').length,
    warning: drives.filter((d) => d.health === 'warning').length,
    failed: drives.filter((d) => ['failed', 'missing'].includes(d.health)).length,
    rebuilding: drives.filter((d) => d.health === 'rebuilding').length,
    spare: drives.filter((d) => d.health === 'spare' || d.role === 'spare').length,
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Loading drives...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <HardDrive size={24} /> Physical Drives
        </h1>
        <button className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800">
          <RefreshCw size={14} /> Rescan
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Total" value={summary.total} icon={<HardDrive size={16} />} />
        <StatCard label="Healthy" value={summary.healthy} icon={<Activity size={16} />} color="text-emerald-600" />
        <StatCard label="Warning" value={summary.warning} icon={<AlertTriangle size={16} />} color="text-amber-600" />
        <StatCard label="Failed" value={summary.failed} icon={<AlertTriangle size={16} />} color="text-rose-600" />
        <StatCard label="Rebuilding" value={summary.rebuilding} icon={<RefreshCw size={16} />} color="text-blue-600" />
        <StatCard label="Spare" value={summary.spare} icon={<HardDrive size={16} />} />
      </div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
            <tr>
              <th className="px-4 py-3 font-medium">Slot</th>
              <th className="px-4 py-3 font-medium">Model</th>
              <th className="px-4 py-3 font-medium">Serial</th>
              <th className="px-4 py-3 font-medium">Capacity</th>
              <th className="px-4 py-3 font-medium">Health</th>
              <th className="px-4 py-3 font-medium">Temp</th>
              <th className="px-4 py-3 font-medium">Power On</th>
              <th className="px-4 py-3 font-medium">Pool</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {drives.map((drive) => (
              <tr key={drive.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td className="px-4 py-3">{drive.slot ?? '-'}</td>
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{drive.model}</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{drive.serial}</td>
                <td className="px-4 py-3">{formatBytes(drive.capacityBytes)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${healthBadge(drive.health)}`}>
                    {drive.health}
                  </span>
                </td>
                <td className="px-4 py-3">{drive.temperatureC}°C</td>
                <td className="px-4 py-3">{drive.powerOnHours.toLocaleString()}h</td>
                <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{drive.pool ?? drive.role}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color = 'text-slate-600 dark:text-slate-400' }: { label: string; value: number; icon: React.ReactNode; color?: string }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm">
      <div className={`flex items-center gap-2 text-sm ${color}`}>{icon} {label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{value}</div>
    </div>
  );
}
